import type { Workspace } from "@brandwall/contracts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useParams, useSearchParams } from "react-router";
import { ApiError, api, patch, post } from "../api";
import { useWorkspace } from "../components/Shell";
import { useToast } from "../components/Toast";

import { PlanTab } from "./SettingsPlan";
import { TeamTab } from "./SettingsTeam";

const SETTINGS_TABS: { id: string; label: string; el: () => React.ReactNode }[] = [
  { id: "team", label: "Team", el: () => <TeamTab /> },
  { id: "plan", label: "Plan and billing", el: () => <PlanTab /> },
];

function Emails() {
  const { ws } = useParams();
  const workspace = useWorkspace();
  const qc = useQueryClient();
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);
  const outbox = useQuery({
    queryKey: ["emails", ws],
    queryFn: () =>
      api<{
        sending: boolean;
        emails: { id: string; subject: string; kind: string; status: string; createdAt: string }[];
      }>(`/v1/w/${ws}/emails`),
  });
  const update = async (b: Partial<Workspace>) => {
    try {
      await patch(`/v1/w/${ws}`, b);
      await qc.invalidateQueries({ queryKey: ["me"] });
      toast("Saved.");
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Something went wrong.");
    }
  };
  const isOwner = workspace.role === "owner";
  return (
    <div className="stack" style={{ maxWidth: 720 }}>
      {error && (
        <div className="error" role="alert">
          {error}
        </div>
      )}
      <section className="card stack">
        <div className="row">
          <div className="spacer">
            <h2>The weekly email</h2>
            <p className="muted">
              Every Monday morning, when there are good new posts, owners and approvers get the best
              ones by email. Approve them all with one tap. On Starter and up.
            </p>
          </div>
          <label className="switch">
            <input
              type="checkbox"
              checked={workspace.weeklyEmail}
              disabled={!isOwner}
              aria-label="Send the weekly email"
              onChange={(e) => update({ weeklyEmail: e.target.checked })}
            />
            <span />
          </label>
        </div>
        <label className="field">
          Your time zone
          <select
            className="input"
            value={workspace.timezone}
            disabled={!isOwner}
            onChange={(e) => update({ timezone: e.target.value })}
          >
            {[
              "Europe/Copenhagen",
              "Europe/Stockholm",
              "Europe/Oslo",
              "Europe/Helsinki",
              "Europe/Berlin",
              "Europe/London",
              "Europe/Amsterdam",
              "UTC",
            ].map((tz) => (
              <option key={tz} value={tz}>
                {tz.replace("_", " ")}
              </option>
            ))}
          </select>
        </label>
        <div className="row wrap">
          <button
            type="button"
            className="btn secondary"
            onClick={async () => {
              const r = await post<{ summary: string }>(`/v1/w/${ws}/emails/weekly`, {});
              toast(r.summary);
              qc.invalidateQueries({ queryKey: ["emails", ws] });
            }}
          >
            Send me this week's email now
          </button>
        </div>
      </section>
      <section className="card">
        <div className="row">
          <div className="spacer">
            <h2>Likes and comments</h2>
            <p className="muted">
              Show how many likes and comments each post has on Instagram, in your Inbox and in
              Discover. Popular posts score a little higher either way.
            </p>
          </div>
          <label className="switch">
            <input
              type="checkbox"
              checked={workspace.showEngagement}
              disabled={!isOwner}
              aria-label="Show likes and comments"
              onChange={(e) => update({ showEngagement: e.target.checked })}
            />
            <span />
          </label>
        </div>
      </section>
      <section className="card stack">
        <h2>Emails to you</h2>
        {outbox.data && !outbox.data.sending && (
          <p className="small muted">
            Brandwall can't send emails yet, so they're kept here for you to read.
          </p>
        )}
        {outbox.data?.emails.length === 0 && <p className="muted">No emails yet.</p>}
        <div className="plist">
          {outbox.data?.emails.map((e) => (
            <div key={e.id} className="prod">
              <span className="spacer">
                <b>{e.subject}</b>
                <span>
                  {new Date(e.createdAt).toLocaleString("en-GB")} ·{" "}
                  {e.status === "logged" ? "kept, not sent" : e.status}
                </span>
              </span>
              <a
                className="btn secondary sm"
                href={`/api/v1/w/${ws}/emails/${e.id}/view`}
                target="_blank"
                rel="noreferrer"
              >
                Open
              </a>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

export function Settings() {
  const [params, setParams] = useSearchParams();
  const tabs = [...SETTINGS_TABS, { id: "emails", label: "Emails", el: () => <Emails /> }];
  const tab = tabs.find((t) => t.id === params.get("tab")) ?? tabs[0]!;
  return (
    <div>
      <div className="tabs" role="tablist">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={t.id === tab.id}
            className={`tab ${t.id === tab.id ? "on" : ""}`}
            onClick={() => setParams({ tab: t.id }, { replace: true })}
          >
            {t.label}
          </button>
        ))}
      </div>
      {tab.el()}
    </div>
  );
}
